import { FormattedDate, FormattedMessage } from "react-intl";

import styled from "styled-components";

import { Table, TablePaginationConfig, Tag, Typography } from "antd";
import { ColumnsType } from "antd/es/table";

import { CmtMissionUser } from "@app/components/missions/CmtMissionUser.tsx";
import { DEFAULT_SIZE } from "@app/constants/paginationConstants.ts";
import { MissionResponse, MissionsPage } from "@app/models/Mission.ts";
import { colors } from "@app/theme.ts";

const StyledTableCard = styled.div`
  overflow: hidden;
  border: 1px solid ${colors.border};
  border-radius: 16px;
  background: ${colors.white};

  .ant-table-pagination.ant-pagination {
    margin-inline: 1rem;
  }
`;

const ExpandedRow = styled.div`
  margin-left: 1rem;
  padding-left: 1rem;
  border-left: 3px solid ${colors.pink};
`;

const MissionDate = ({ value }: { value: string | null }) =>
  value ? <FormattedDate value={new Date(value)} dateStyle="medium" /> : "-";

const dateColumns: ColumnsType<MissionResponse> = [
  {
    title: (
      <FormattedMessage id="table.start_date" defaultMessage="Start Date" />
    ),
    dataIndex: "startDate",
    key: "startDate",
    render: (startDate: string) => <MissionDate value={startDate} />,
  },
  {
    title: <FormattedMessage id="table.end_date" defaultMessage="End Date" />,
    dataIndex: "endDate",
    key: "endDate",
    render: (endDate: string | null) => <MissionDate value={endDate} />,
  },
];

const columns: ColumnsType<MissionResponse> = [
  {
    title: <FormattedMessage id="table.client_name" defaultMessage="Client" />,
    dataIndex: "clientName",
    key: "clientName",
    render: (clientName: string, mission) => (
      <>
        <Typography.Text strong>{clientName}</Typography.Text>
        <div>
          <Typography.Text type="secondary">{mission.name}</Typography.Text>
        </div>
      </>
    ),
  },
  {
    title: (
      <FormattedMessage id="table.takima_user" defaultMessage="Employee" />
    ),
    dataIndex: "takimaUserEmail",
    key: "takimaUserEmail",
    render: (email: string, mission) => (
      <CmtMissionUser email={email} user={mission.takimaUser} />
    ),
  },
  ...dateColumns,
  {
    title: (
      <FormattedMessage id="table.mission_ended" defaultMessage="Finished" />
    ),
    dataIndex: "missionEnded",
    key: "missionEnded",
    render: (missionEnded: boolean) =>
      missionEnded ? (
        <Tag>
          <FormattedMessage
            id="table.mission_ended"
            defaultMessage="Finished"
          />
        </Tag>
      ) : (
        <Tag color="green">
          <FormattedMessage
            id="collaborator.missions.ongoing"
            defaultMessage="Ongoing"
          />
        </Tag>
      ),
  },
];

const subColumns: ColumnsType<MissionResponse> = [
  ...dateColumns,
  {
    title: (
      <FormattedMessage id="table.mission_name" defaultMessage="Mission" />
    ),
    dataIndex: "name",
    key: "name",
  },
];

interface CmtMissionsTableProps {
  missions?: MissionsPage;
  isLoading: boolean;
  onChange: (pagination: TablePaginationConfig) => void;
  currentPage: number;
}

export const CmtMissionsTable = ({
  missions,
  isLoading,
  onChange,
  currentPage,
}: CmtMissionsTableProps) => {
  return (
    <StyledTableCard>
      <Table<MissionResponse>
        columns={columns}
        dataSource={missions?.content}
        rowKey="id"
        pagination={{
          current: currentPage,
          total: missions?.totalElements,
          pageSize: DEFAULT_SIZE,
          showSizeChanger: false,
        }}
        loading={isLoading}
        onChange={onChange}
        expandable={{
          expandedRowRender: (mission) => (
            <ExpandedRow>
              <Table<MissionResponse>
                columns={subColumns}
                dataSource={mission.prolongations}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </ExpandedRow>
          ),
          rowExpandable: (mission) => mission.prolongations?.length > 0,
        }}
      />
    </StyledTableCard>
  );
};
